import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import DataGrid, {
	Scrolling,
	SearchPanel,
	Grouping,
	GroupPanel,
	Sorting,
	Selection,
	StateStoring, LoadPanel
} from 'devextreme-react/data-grid'
import { useTranslation } from 'react-i18next'
import ContentHeader from 'components/ContenHeader'
import { toolbarCustomizer, columnsRender } from './utils'
import useLocale from 'hooks/useLocale'
import StyledWrapper from './styled'
import { LoadingComponent } from 'components/DataTable/components'
import 'devextreme/dist/css/dx.common.css'
import 'devextreme/dist/css/dx.light.css'
import isEqual from 'lodash/isEqual'


const DxDataTable = props => {
	const { data, columns, height, onRowClick, withSearch, groupBy, manualGroup, customStoring, label, emptyNoDataText, children, onToolbarPreparing, templates, loading, onSelectionChanged, onSaveSettings, settings, toolBarActions, withColFilter, sortByGroupSummaryInfo, ...restProps } = props
	const DataGridRef = useRef()
	const [t] = useTranslation()
	const locales = useLocale()
	const [focusedKey, setFocusedKey] = useState(null)
	const { onToolbarPreparing: customToolbar, templates: customTemplates } = toolbarCustomizer({ ...props, onToolbarPreparing, templates, ref: DataGridRef, toolBarActions })
	const { cols, footerCols } = useMemo(() => columnsRender({ columns: typeof columns === 'function' ? columns(t) : columns, groupBy, locales }), [columns, groupBy])
	const dataGridHeight = data?.length ? height : 'auto'
	useEffect(() => {
		if(props.getRef) {
			props.getRef(DataGridRef.current)
		}
	}, [])
	const saveState = useCallback((state) => {
		if(!isEqual(state?.columns, settings?.columns)) {
			onSaveSettings(state)
		}
	}, [settings])

	const handleOptionChanged = useCallback((e) => {
		if(!Array.isArray(groupBy) || !groupBy.length) {
			return
		}
		if(!e || !e.name || e.name !== 'columns' || !e.fullName) {
			return
		}

		if(!/\.sortOrder$/.test(e.fullName)) {
			return
		}
		const sortOrder = e.value
		const srcColumns = typeof columns === 'function' ? columns(t) : columns
		const match = e.fullName.match(/^columns\[(\d+)\]\.sortOrder$/)
		const colIndex = match ? Number(match[1]) : -1
		const srcCol = srcColumns && srcColumns[colIndex]
		if(!srcCol) {
			return
		}

		if(srcCol.groupSummary) {
			const summaryName = srcCol.groupSummary.name || srcCol.dataField || srcCol.caption
			const info = sortOrder ? [{ summaryItem: summaryName, groupColumn: groupBy[0], sortOrder }] : []
			try {
				DataGridRef.current.instance.option('sortByGroupSummaryInfo', info)
			} catch(err) {
				// ignore
			}
		}
	}, [groupBy, columns])

	const onFocusedRowChanged = (rowData) => {
		const nextKey = rowData.row?.key || null
		if (nextKey === focusedKey) {
			return
		}
		setFocusedKey(nextKey)
		if(rowData.row) {
			onRowClick(rowData?.row?.data, rowData)
		}
	}

	return(
		<StyledWrapper {...props}>
			{label && <ContentHeader title={label} />}
			<DataGrid
				key={dataGridHeight}
				dataSource={data}
				height={dataGridHeight}
				ref={DataGridRef}
				onToolbarPreparing={customToolbar}
				{...sortByGroupSummaryInfo && { onOptionChanged: handleOptionChanged }}
				{...onRowClick && {
					keyExpr: 'id',
					focusedRowEnabled: true,
					autoNavigateToFocusedRow: false,
					focusedRowKey: focusedKey,
					onFocusedRowChanged
				}}
				columnAutoWidth
				allowColumnResizing
				allowColumnReordering
				showColumnLines={false}
				noDataText={emptyNoDataText ? '' : t('common.strings.table_no_data')}
				sorting={{
					ascendingText: t('common.strings.sort_asc'),
					descendingText: t('common.strings.sort_desc'),
					clearText: t('common.strings.clear_sort')
				}}
				onSelectionChanged={typeof onSelectionChanged === 'function' ? onSelectionChanged : null}
				columnChooser={withColFilter ? {
					enabled: true,
					mode: 'select',
					title: t('common.strings.table_column_filter'),
					width: 400
				} : null}
				{...restProps}
			>
				<Selection mode={onSelectionChanged ? 'multiple' : 'none'} />
				<Scrolling mode='virtual' />
				{withSearch &&
					<SearchPanel
						visible={false}
						highlightSearchText={false}
					/>
				}
				{cols}
				{data?.length ? footerCols : null}
				{customStoring && <StateStoring enabled={true} type='custom' customSave={saveState} customLoad={() => settings} />}
				<Grouping expandMode='rowClick' autoExpandAll={false} />
				<LoadPanel enabled={false}/>
				{manualGroup && <GroupPanel visible={true} emptyPanelText={t('common.strings.drag_col_to_group')} />}
				<Sorting mode='multiple' />
				{customTemplates}
				{children}
			</DataGrid>
			<LoadingComponent loading={loading} />
		</StyledWrapper>
	)
}

DxDataTable.defaultProps = {
	data: [],
	exportFileName: 'Data',
	withSearch: true
}

DxDataTable.propTypes = {
	data: PropTypes.array.isRequired,
	columns: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
	height: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.func]),
	withSearch: PropTypes.bool,
	onRowClick: PropTypes.func,
	label: PropTypes.string,
	manualGroup: PropTypes.bool,
	excelExport: PropTypes.bool,
	exportFileName: PropTypes.string,
	customStoring: PropTypes.bool,
	toolBarActions: PropTypes.array,
	sortByGroupSummaryInfo: PropTypes.bool
}

export default DxDataTable
